import React, { useEffect, useState } from "react"
import "./StatePreferences.scss"
import {
  Col,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Button,
  Spinner,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import StatePreferenceApi from "Api/StatePreferenceApi"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import { useCallback, useRef } from "react"

const StatePreferences = () => {
  document.title = "State Preferences"
  const breadcrumbItems = [
    { label: "Pixwik", link: "#" },
    { label: "Applications", link: "#" },
    { label: "State Preferences", link: "#" },
  ]

  const [states, setStates] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [limit, setLimit] = useState(10)
  const [editingId, setEditingId] = useState(null)
  const [editingValue, setEditingValue] = useState("")
  const [modal, setModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const accessToken = localStorage.getItem("accessToken")

  const initialData = {
    name: "",
    countryCode: "",
    preferenceNumber: "",
  }

  const [stateData, setStateData] = useState(initialData)

  const fetchStatePreferences = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        limit: limit,
        page: page,
      },
    }
    StatePreferenceApi.getAllState(config)
      .then(response => {
        if (response.data && Array.isArray(response.data.data)) {
          setStates(response.data.data)
          setTotalPages(response.data.pagination.totalPages)
        } else {
          console.log("API Response:", response.data)
          setStates([])
          setTotalPages(0)
        }
        setLoading(false)
      })
      .catch(error => {
        console.error("Error fetching data:", error)
        setLoading(false)
        setStates([])
      })
  }

  useEffect(() => {
    fetchStatePreferences()
  }, [page, limit])

  const handlePreferenceClick = state => {
    setEditingId(state._id)
    setEditingValue(state.preferenceNumber)
  }

  const handleInputChange = e => {
    setEditingValue(e.target.value)
  }

  const handleInputBlurOrEnter = state => {
    const query = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
    if (
      editingValue !== "" &&
      !isNaN(editingValue) &&
      Number(editingValue) > 0 &&
      Number(editingValue) !== state.preferenceNumber
    ) {
      StatePreferenceApi.updateStatePreference(
        {
          stateId: state._id,
          preferenceNumber: Number(editingValue),
        },
        query,
      )
        .then(() => {
          fetchStatePreferences()
          setEditingId(null)
          setEditingValue("")
        })
        .catch(err => {
          // Optionally handle error
        })
    }
    setEditingId(null)
    setEditingValue("")
  }

  const [searchQuery, setSearchQuery] = useState("")

  const debounce = (func, delay) => {
    let timer
    return (...args) => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        func(...args)
      }, delay)
    }
  }

  const debouncedSearchRef = useRef()

  const searchStates = useCallback(
    query => {
      if (query.trim() === "") {
        fetchStatePreferences()
      } else {
        const config = {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
        StatePreferenceApi.searchState({ query }, config)
          .then(response => {
            console.log("API Response:", response.data)
            if (response.data && Array.isArray(response.data.data)) {
              setStates(response.data.data)
              if (
                response.data.pagination &&
                response.data.pagination.totalPages
              ) {
                setTotalPages(response.data.pagination.totalPages)
              } else {
                setTotalPages(1) // or 0 if you want to hide pagination
              }
            } else {
              console.log("API Response:", response.data)
              setStates([])
              setTotalPages(0)
            }
          })
          .catch(error => {
            console.error("Error searching states:", error)
            setStates([])
            setTotalPages(0)
          })
      }
    },
    [fetchStatePreferences, accessToken],
  )

  if (!debouncedSearchRef.current) {
    debouncedSearchRef.current = debounce(searchStates, 800)
  }

  const handleSearch = useCallback(
    e => {
      const query = e.target.value
      setSearchQuery(query)
      debouncedSearchRef.current(query)
    },
    [setSearchQuery],
  )

  const toggleModal = () => {
    setModal(!modal)
    if (!modal) {
      setStateData(initialData)
    }
  }

  const handleFormInputChange = (field, value) => {
    setStateData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleAddState = () => {
    if (!stateData.name || !stateData.countryCode || !stateData.preferenceNumber) {
      return
    }

    setSaving(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
    
    StatePreferenceApi.addStatePreference(stateData, config)
      .then(response => {
        console.log(response.data)
        setStates([...states, response.data.data])
        setStateData(initialData)
        toggleModal()
      })
      .catch(error => {
        console.log(error)
      })
      .finally(() => {
        setSaving(false)
        setStateData(initialData)
      })
  }

  return (
    <div className="state-preferences">
      <Row>
        <Col xs="12">
          <div className="list-header d-flex align-items-center justify-content-between">
            <CustomBreadCrumb
              data={breadcrumbItems}
              pageTitle="State Preferences"
            />

            <div className="d-flex align-items-center gap-3">
              <Input
                type="text"
                placeholder="Search State"
                className="form-control"
                style={{ width: "300px" }}
                value={searchQuery}
                onChange={handleSearch}
              />
              <Button color="primary" onClick={toggleModal}>
                Add State
              </Button>
            </div>
          </div>

          <div className="data-table table-responsive mt-4 mb-3">
            {loading ? (
              <DataTableLoader cols={7} />
            ) : (
              <Table className="table mb-0">
                <thead className="table-light">
                  <tr>
                    <th>State Name</th>
                    <th>Country Code</th>
                    <th>Preference Number</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(states) &&
                    states.map((state, index) => (
                      <tr key={state._id}>
                        <td>{state.name}</td>
                        <td>{state.countryCode}</td>
                        <td
                          style={{
                            cursor: "pointer",
                            color: "#007bff",
                          }}
                        >
                          {editingId === state._id ? (
                            <input
                              type="number"
                              min={1}
                              value={editingValue}
                              autoFocus
                              onChange={handleInputChange}
                              onBlur={() => handleInputBlurOrEnter(state)}
                              onKeyDown={e => {
                                if (e.key === "Enter") {
                                  handleInputBlurOrEnter(state)
                                }
                              }}
                              style={{ width: 60 }}
                            />
                          ) : (
                            <span
                              onClick={() => handlePreferenceClick(state)}
                            >
                              {state.preferenceNumber}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            )}
          </div>

          {totalPages > 1 && (
            <div className="pagination-container d-flex align-items-center justify-content-end">
              <Pagination>
                <PaginationItem disabled={page <= 1}>
                  <PaginationLink previous onClick={() => setPage(page - 1)} />
                </PaginationItem>
                {[...Array(Math.min(3, totalPages))].map((_, index) => (
                  <PaginationItem key={index} active={index + 1 === page}>
                    <PaginationLink onClick={() => setPage(index + 1)}>
                      {index + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                {totalPages > 3 && (
                  <PaginationItem disabled>
                    <PaginationLink>...</PaginationLink>
                  </PaginationItem>
                )}
                <PaginationItem disabled={page >= totalPages}>
                  <PaginationLink next onClick={() => setPage(page + 1)} />
                </PaginationItem>
              </Pagination>
            </div>
          )}
        </Col>
      </Row>

      {/* Add State Modal */}
      <Modal isOpen={modal} toggle={toggleModal} size="md">
        <ModalHeader toggle={toggleModal}>
          Add New State
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="stateName">State Name</Label>
              <Input
                id="stateName"
                type="text"
                value={stateData.name}
                onChange={(e) => handleFormInputChange('name', e.target.value)}
                placeholder="Enter state name"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="countryCode">Country Code</Label>
              <Input
                id="countryCode"
                type="text"
                value={stateData.countryCode}
                onChange={(e) => handleFormInputChange('countryCode', e.target.value)}
                placeholder="Enter country code (e.g., US, UK)"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="preferenceNumber">Preference Number</Label>
              <Input
                id="preferenceNumber"
                type="number"
                min="1"
                value={stateData.preferenceNumber}
                onChange={(e) => handleFormInputChange('preferenceNumber', e.target.value)}
                placeholder="Enter preference number"
                required
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal} disabled={saving}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            onClick={handleAddState} 
            disabled={saving || !stateData.name || !stateData.countryCode || !stateData.preferenceNumber}
          >
            {saving ? <Spinner size="sm" /> : 'Add State'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default StatePreferences